import { useState } from 'react';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  Input,
  LoadingSkeleton,
  Modal,
  ProgressBar,
  Select,
} from '@nexora/design-system';
import {
  useClosingChecklist,
  useClosingPeriods,
  useUpdateChecklistItem,
} from './useClosing';
import {
  CLOSING_FLOW_STEPS,
  ETAPA_WORKFLOW_LABEL,
  STATUS_CHECKLIST_LABEL,
  type ChecklistAcao,
  type ChecklistItem,
  type ChecklistItemStatus,
  type DadosBancarios,
} from './types';
import { formatCount, formatCurrency, formatDateTime } from '../../shared/utils/format';

/* ═══════════════════════════════════════════════════════════════════════════
   Workflow de Fechamento — Checklist (5 etapas) + Conciliação Bancária
   ───────────────────────────────────────────────────────────────────────────
   Contrato consumido (rota real /api/v1/closing):

   GET /closing/periods                    → Paginated<FechamentoPeriodo>
     (seletor de período; cada item: { id, label, status, ... })

   GET /closing/periods/:id/checklist { limit } → Paginated<ChecklistItem>
     ChecklistItem: { id, periodId, etapa ("1 Module Closure" ... "5 Accounting
       Consolidation"), ordem, item, status (PENDENTE|EM_ANDAMENTO|CONCLUIDO|
       NAO_APLICAVEL), responsavel, evidencias, bankName, ledgerBalance,
       statementBalance, bankVariance, concluidoEm, concluidoPor, ... }

   PATCH /closing/periods/:id/checklist/:itemId { acao, dadosBancarios? }
     { acao: 'INICIAR' | 'CONCLUIDO' | 'NAO_APLICAVEL' | 'REABRIR',
       dadosBancarios?: { bankName, ledgerBalance, statementBalance } }

   A etapa "2 Bank Conciliation" carrega os campos do passo 2 da tela de
   referência: Account Name (item), Bank (bankName), Ledger Balance
   (ledgerBalance), Statement Balance (statementBalance) e a Variance
   (bankVariance = ledger - statement, calculada pelo backend). A ação
   "Conciliar" grava os valores via PATCH com acao CONCLUIDO.
   ═══════════════════════════════════════════════════════════════════════════ */

const STATUS_CHECKLIST_BADGE: Record<
  ChecklistItemStatus,
  'success' | 'warning' | 'error' | 'info' | 'neutral'
> = {
  PENDENTE: 'warning',
  EM_ANDAMENTO: 'info',
  CONCLUIDO: 'success',
  NAO_APLICAVEL: 'neutral',
};

/** Status agregado de uma etapa a partir dos itens: concluída quando todos
    fecharam; em andamento quando há algum iniciado; senão pendente. */
function stepStatus(items: ChecklistItem[]): ChecklistItemStatus {
  if (items.length === 0) return 'PENDENTE';
  if (items.every((i) => i.status === 'CONCLUIDO' || i.status === 'NAO_APLICAVEL')) return 'CONCLUIDO';
  if (items.some((i) => i.status === 'EM_ANDAMENTO')) return 'EM_ANDAMENTO';
  return 'PENDENTE';
}

function etapaInfo(etapa: string): { titulo: string; descricao: string } {
  return ETAPA_WORKFLOW_LABEL[etapa] ?? { titulo: etapa, descricao: '' };
}

/* ── Modal de conciliação bancária (Step 2) ── */

interface DadosBancariosForm {
  bankName: string;
  ledgerBalance: string;
  statementBalance: string;
}

function BankConciliationModal({
  item,
  onClose,
  onSave,
}: {
  item: ChecklistItem;
  onClose: () => void;
  onSave: (dados: DadosBancarios) => Promise<void>;
}): JSX.Element {
  const [form, setForm] = useState<DadosBancariosForm>({
    bankName: item.bankName ?? '',
    ledgerBalance: item.ledgerBalance != null ? String(item.ledgerBalance) : '',
    statementBalance: item.statementBalance != null ? String(item.statementBalance) : '',
  });
  const [saving, setSaving] = useState(false);

  const ledger = Number.parseFloat(form.ledgerBalance.replace(',', '.'));
  const statement = Number.parseFloat(form.statementBalance.replace(',', '.'));
  const variance = Number.isFinite(ledger) && Number.isFinite(statement) ? ledger - statement : null;

  const handleSave = async (): Promise<void> => {
    setSaving(true);
    try {
      await onSave({
        bankName: form.bankName.trim() || null,
        ledgerBalance: Number.isFinite(ledger) ? ledger : null,
        statementBalance: Number.isFinite(statement) ? statement : null,
      });
      onClose();
    } catch {
      /* erro tratado pelo chamador (alert) */
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      isOpen
      onClose={onClose}
      title="Conciliação Bancária"
      size="md"
      footer={
        <>
          <Button variant="ghost" size="md" onClick={onClose} disabled={saving}>
            Cancelar
          </Button>
          <Button variant="primary" size="md" onClick={handleSave} loading={saving}>
            Confirmar conciliação
          </Button>
        </>
      }
    >
      <p className="font-body-sm text-body-sm text-on-surface-variant m-0" style={{ marginBottom: '1rem' }}>
        Conta: <strong className="text-on-surface">{item.item}</strong>
      </p>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
        <div style={{ gridColumn: '1 / -1' }}>
          <Input
            label="Banco"
            placeholder="Ex.: Banco do Brasil"
            value={form.bankName}
            onChange={(e) => setForm({ ...form, bankName: e.target.value })}
          />
        </div>
        <Input
          label="Ledger Balance (R$)"
          type="text"
          inputMode="decimal"
          placeholder="0,00"
          value={form.ledgerBalance}
          onChange={(e) => setForm({ ...form, ledgerBalance: e.target.value })}
        />
        <Input
          label="Statement Balance (R$)"
          type="text"
          inputMode="decimal"
          placeholder="0,00"
          value={form.statementBalance}
          onChange={(e) => setForm({ ...form, statementBalance: e.target.value })}
        />
      </div>
      <div
        style={{
          marginTop: '1rem',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '0.75rem 1rem',
          background: '#f2f4f6',
          borderRadius: '0.5rem',
          fontSize: '0.875rem',
        }}
      >
        <span className="text-on-surface-variant">Variance (Ledger − Statement)</span>
        <strong style={{ color: variance != null && variance !== 0 ? '#ba1a1a' : '#2e7d32' }}>
          {variance != null ? formatCurrency(variance) : '—'}
        </strong>
      </div>
    </Modal>
  );
}

/* ── Ações por status do item ── */

function ItemActions({
  item,
  onAction,
  onConciliar,
}: {
  item: ChecklistItem;
  onAction: (item: ChecklistItem, acao: ChecklistAcao) => void;
  onConciliar: (item: ChecklistItem) => void;
}): JSX.Element {
  const isBank = item.etapa === '2 Bank Conciliation';
  if (item.status === 'PENDENTE') {
    return (
      <>
        {isBank ? (
          <Button variant="primary" size="sm" onClick={() => onConciliar(item)}>
            Conciliar
          </Button>
        ) : (
          <Button variant="outline" size="sm" onClick={() => onAction(item, 'INICIAR')}>
            Iniciar
          </Button>
        )}
        <Button variant="ghost" size="sm" onClick={() => onAction(item, 'NAO_APLICAVEL')}>
          N/A
        </Button>
      </>
    );
  }
  if (item.status === 'EM_ANDAMENTO') {
    return (
      <>
        {isBank ? (
          <Button variant="primary" size="sm" onClick={() => onConciliar(item)}>
            Conciliar
          </Button>
        ) : (
          <Button variant="primary" size="sm" onClick={() => onAction(item, 'CONCLUIDO')}>
            Concluir
          </Button>
        )}
        <Button variant="ghost" size="sm" onClick={() => onAction(item, 'NAO_APLICAVEL')}>
          N/A
        </Button>
      </>
    );
  }
  return (
    <Button variant="ghost" size="sm" onClick={() => onAction(item, 'REABRIR')}>
      Reabrir
    </Button>
  );
}

/* ── Página ── */

export function ClosingChecklistPage(): JSX.Element {
  const [periodId, setPeriodId] = useState<string | undefined>(undefined);
  const [bankItem, setBankItem] = useState<ChecklistItem | null>(null);

  const periodsQuery = useClosingPeriods({ limit: 50 });
  const periods = periodsQuery.data?.data ?? [];
  const activePeriodId = periodId ?? periods[0]?.id;

  const checklistQuery = useClosingChecklist(activePeriodId, { limit: 100 });
  const updateItem = useUpdateChecklistItem(activePeriodId);

  const items = checklistQuery.data?.data ?? [];

  /* Agrupa por etapa na ordem canônica; etapas desconhecidas vão ao final. */
  const porEtapa = new Map<string, ChecklistItem[]>();
  items.forEach((i) => {
    porEtapa.set(i.etapa, [...(porEtapa.get(i.etapa) ?? []), i]);
  });
  const grupos = [
    ...CLOSING_FLOW_STEPS.filter((e) => porEtapa.has(e)).map((e) => ({
      etapa: e,
      itens: porEtapa.get(e) ?? [],
    })),
    ...[...porEtapa.keys()]
      .filter((e) => !CLOSING_FLOW_STEPS.includes(e as (typeof CLOSING_FLOW_STEPS)[number]))
      .map((e) => ({ etapa: e, itens: porEtapa.get(e) ?? [] })),
  ];

  const concluidos = items.filter((i) => i.status === 'CONCLUIDO' || i.status === 'NAO_APLICAVEL').length;

  const handleAction = async (item: ChecklistItem, acao: ChecklistAcao): Promise<void> => {
    try {
      await updateItem.mutateAsync({ itemId: item.id, input: { acao } });
    } catch (err) {
      window.alert(err instanceof Error ? err.message : 'Erro ao atualizar item do checklist.');
    }
  };

  const handleConciliar = async (dados: DadosBancarios): Promise<void> => {
    if (!bankItem) return;
    try {
      await updateItem.mutateAsync({ itemId: bankItem.id, input: { acao: 'CONCLUIDO', dadosBancarios: dados } });
    } catch (err) {
      window.alert(err instanceof Error ? err.message : 'Erro ao concluir conciliação bancária.');
      throw err;
    }
  };

  return (
    <div className="closing-checklist-page">
      {/* Cabeçalho */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-md mb-lg">
        <div>
          <p className="font-label-sm text-label-sm text-on-surface-variant mb-1 uppercase tracking-wider">
            Fechamento Mensal / Workflow
          </p>
          <h1 className="font-display-lg text-display-lg text-primary m-0">Checklist do Fechamento</h1>
          <p className="font-body-md text-body-md text-on-surface-variant mt-xs">
            Cinco etapas de encerramento do período, incluindo a conciliação bancária (Passo 2).
          </p>
        </div>
        <div style={{ flex: '0 0 280px' }}>
          <Select
            label="Período de fechamento"
            value={activePeriodId ?? ''}
            onChange={(e) => setPeriodId(e.target.value)}
            selectSize="sm"
            placeholder="Selecione um período"
          >
            {periods.map((p) => (
              <option key={p.id} value={p.id}>
                {p.label}
              </option>
            ))}
          </Select>
        </div>
      </div>

      {/* Erro */}
      {(periodsQuery.error || checklistQuery.error) && (
        <Card variant="outlined" padding="1.5rem" className="mb-lg">
          <p className="m-0" style={{ fontWeight: 600, color: '#ba1a1a' }}>
            Erro ao carregar o checklist
          </p>
          <p className="m-0" style={{ fontSize: '0.875rem', color: '#74777f', marginTop: '0.25rem' }}>
            {periodsQuery.error instanceof Error
              ? periodsQuery.error.message
              : checklistQuery.error instanceof Error
                ? checklistQuery.error.message
                : 'Erro desconhecido'}
          </p>
          <Button
            variant="primary"
            size="sm"
            onClick={() => {
              periodsQuery.refetch();
              checklistQuery.refetch();
            }}
            style={{ marginTop: '0.75rem' }}
          >
            Tentar novamente
          </Button>
        </Card>
      )}

      {/* Progresso geral */}
      {!checklistQuery.error && (
        <Card variant="outlined" padding="lg" className="mb-lg">
          <div className="flex justify-between font-label-md text-label-md text-on-surface-variant mb-xs">
            <span>Progresso do período</span>
            <span className="font-bold text-primary">
              {concluidos} de {items.length} itens concluídos
            </span>
          </div>
          <ProgressBar value={checklistQuery.isLoading ? 0 : items.length === 0 ? 0 : (concluidos / items.length) * 100} max={100} variant="default" size="sm" />
        </Card>
      )}

      {/* Etapas */}
      {checklistQuery.isLoading && (
        <div style={{ marginBottom: '1rem' }}>
          <LoadingSkeleton count={3} height="160px" borderRadius="12px" />
        </div>
      )}

      {!checklistQuery.isLoading && !checklistQuery.error && grupos.length === 0 && (
        <Card variant="outlined" padding="lg">
          <div style={{ textAlign: 'center', color: '#74777f' }}>
            <p className="m-0" style={{ fontSize: '0.875rem' }}>
              Nenhum item de checklist para este período.
            </p>
          </div>
        </Card>
      )}

      {!checklistQuery.isLoading &&
        !checklistQuery.error &&
        grupos.map((grupo) => {
          const info = etapaInfo(grupo.etapa);
          const total = grupo.itens.length;
          const feitos = grupo.itens.filter((i) => i.status === 'CONCLUIDO' || i.status === 'NAO_APLICAVEL').length;
          const isBank = grupo.etapa === '2 Bank Conciliation';
          return (
            <Card key={grupo.etapa} variant="outlined" padding="0" className="mb-lg">
              <CardHeader
                className="px-md pt-md"
                action={
                  <div className="flex items-center gap-sm">
                    <Badge
                      variant={STATUS_CHECKLIST_BADGE[stepStatus(grupo.itens)]}
                      size="sm"
                      dot
                    >
                      {STATUS_CHECKLIST_LABEL[stepStatus(grupo.itens)]}
                    </Badge>
                    <span className="font-label-md text-label-md text-on-surface-variant tabular-nums">
                      {feitos}/{total}
                    </span>
                  </div>
                }
              >
                <h3 className="font-headline-sm text-headline-sm text-on-surface m-0">
                  Passo {grupo.etapa.charAt(0)} — {info.titulo}
                </h3>
                <p className="font-body-sm text-body-sm text-on-surface-variant m-0" style={{ marginTop: '0.125rem' }}>
                  {info.descricao}
                </p>
              </CardHeader>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
                  <thead>
                    <tr>
                      {['Item', ...(isBank ? ['Banco', 'Ledger (R$)', 'Statement (R$)', 'Variance (R$)'] : ['Responsável']), 'Status', 'Ações'].map(
                        (h) => (
                          <th
                            key={h}
                            style={{
                              textAlign: 'left',
                              padding: '0.75rem 1rem',
                              background: '#f2f4f6',
                              color: '#43474e',
                              fontWeight: 600,
                              fontSize: '0.75rem',
                              textTransform: 'uppercase',
                              letterSpacing: '0.05em',
                              borderBottom: '2px solid #e0e3e5',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {h}
                          </th>
                        ),
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {grupo.itens.map((item) => (
                      <tr key={item.id}>
                        <td style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #eceef0' }}>
                          <div style={{ fontWeight: 600, color: '#191c1e' }}>{item.item}</div>
                          {item.concluidoEm && (
                            <div className="font-label-sm text-label-sm text-on-surface-variant">
                              Concluído em {formatDateTime(item.concluidoEm)} por {item.concluidoPor ?? '—'}
                            </div>
                          )}
                        </td>
                        {isBank ? (
                          <>
                            <td style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #eceef0' }}>
                              {item.bankName ?? '—'}
                            </td>
                            <td style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #eceef0', whiteSpace: 'nowrap' }}>
                              {item.ledgerBalance != null ? formatCurrency(item.ledgerBalance) : '—'}
                            </td>
                            <td style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #eceef0', whiteSpace: 'nowrap' }}>
                              {item.statementBalance != null ? formatCurrency(item.statementBalance) : '—'}
                            </td>
                            <td
                              style={{
                                padding: '0.75rem 1rem',
                                borderBottom: '1px solid #eceef0',
                                whiteSpace: 'nowrap',
                                fontWeight: 600,
                                color: item.bankVariance != null && item.bankVariance !== 0 ? '#ba1a1a' : '#2e7d32',
                              }}
                            >
                              {item.bankVariance != null ? formatCurrency(item.bankVariance) : '—'}
                            </td>
                          </>
                        ) : (
                          <td style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #eceef0' }}>
                            {item.responsavel ?? '—'}
                            {item.evidencias.length > 0 && (
                              <span className="font-label-sm text-label-sm text-on-surface-variant ml-sm">
                                ({formatCount(item.evidencias.length)} evid.)
                              </span>
                            )}
                          </td>
                        )}
                        <td style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #eceef0' }}>
                          <Badge variant={STATUS_CHECKLIST_BADGE[item.status]} size="sm">
                            {STATUS_CHECKLIST_LABEL[item.status]}
                          </Badge>
                        </td>
                        <td style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #eceef0', whiteSpace: 'nowrap' }}>
                          <ItemActions item={item} onAction={handleAction} onConciliar={setBankItem} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          );
        })}

      {bankItem && (
        <BankConciliationModal key={bankItem.id} item={bankItem} onClose={() => setBankItem(null)} onSave={handleConciliar} />
      )}
    </div>
  );
}